import { useState, useEffect } from 'react';
import { useInstallPWA } from '../../hooks/useInstallPWA';

// ─── iOS Install Instructions Modal ───────────────────────────────────────────
const IOSInstructionsModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  if (!open) return null;

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm mx-0 sm:mx-4 p-6 pb-8 sm:pb-6 animate-slide-up">
        {/* Handle */}
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5 sm:hidden" />

        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-2xl flex items-center justify-center">
              <i className="ri-smartphone-line text-white text-lg" />
            </div>
            <div>
              <h3 className="text-base font-700 text-gray-900">Adicionar à tela inicial</h3>
              <p className="text-xs text-gray-400">
                {isAndroid ? 'Instruções para Android' : 'Instruções para iPhone/iPad'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* iOS Steps */}
        {isIOS && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-700 text-emerald-700">1</span>
              </div>
              <div>
                <p className="text-sm font-600 text-gray-800">Toque no botão Compartilhar</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  O ícone{' '}
                  <span className="inline-flex items-center gap-0.5 font-600 text-gray-700">
                    <i className="ri-share-forward-box-line text-sm" /> Compartilhar
                  </span>{' '}
                  fica na barra inferior do Safari
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-700 text-emerald-700">2</span>
              </div>
              <div>
                <p className="text-sm font-600 text-gray-800">Role para baixo e toque em</p>
                <div className="mt-1.5 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-xl">
                  <i className="ri-add-box-line text-gray-600 text-base" />
                  <span className="text-sm font-600 text-gray-700">Adicionar à Tela de Início</span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-700 text-emerald-700">3</span>
              </div>
              <div>
                <p className="text-sm font-600 text-gray-800">Confirme tocando em Adicionar</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  O ícone do PandaPro aparecerá na sua tela inicial
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
              <i className="ri-information-line text-amber-500 text-sm shrink-0" />
              <p className="text-xs text-amber-700">
                Use o <span className="font-600">Safari</span> para adicionar à tela inicial. Outros navegadores no iPhone não suportam essa função.
              </p>
            </div>
          </div>
        )}

        {/* Android / Generic Steps */}
        {!isIOS && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-700 text-emerald-700">1</span>
              </div>
              <div>
                <p className="text-sm font-600 text-gray-800">Toque no menu do navegador</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Os três pontos <i className="ri-more-2-fill" /> no canto superior direito do Chrome
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-700 text-emerald-700">2</span>
              </div>
              <div>
                <p className="text-sm font-600 text-gray-800">Toque em</p>
                <div className="mt-1.5 inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-xl">
                  <i className="ri-add-circle-line text-gray-600 text-base" />
                  <span className="text-sm font-600 text-gray-700">Adicionar à tela inicial</span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-7 h-7 bg-emerald-50 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-700 text-emerald-700">3</span>
              </div>
              <div>
                <p className="text-sm font-600 text-gray-800">Confirme e toque em Adicionar</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  O PandaPro será instalado como app na sua tela inicial
                </p>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full mt-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-2xl text-sm cursor-pointer whitespace-nowrap transition-colors"
        >
          Entendido
        </button>
      </div>
    </div>
  );
};

// ─── Main Install Banner (bottom slide-in) ────────────────────────────────────
const InstallBannerContent = () => {
  const { showBanner, isIOSDevice, installState, triggerInstall, dismiss, showIOSModal, closeIOSModal } =
    useInstallPWA();

  const [visible, setVisible] = useState(false);

  // Animate in after mount
  useEffect(() => {
    if (showBanner) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [showBanner]);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(dismiss, 300);
  };

  const handleAdd = async () => {
    await triggerInstall();
    // After triggering, mark as shown so it doesn't appear again
    dismiss();
  };

  if (!showBanner && !showIOSModal) return null;

  return (
    <>
      {/* Bottom banner */}
      {showBanner && (
        <div
          className={`fixed bottom-0 left-0 right-0 z-[80] px-4 pb-6 pt-2 transition-all duration-300 ease-out ${
            visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
          }`}
        >
          <div className="bg-white rounded-3xl border border-gray-100 p-4 max-w-md mx-auto"
            style={{ boxShadow: '0 -2px 24px rgba(0,0,0,0.08), 0 4px 24px rgba(0,0,0,0.1)' }}>
            {/* Icon + text */}
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 bg-emerald-500 rounded-2xl flex items-center justify-center shrink-0">
                <i className="ri-home-4-line text-white text-xl" />
              </div>
              <div className="flex-1 min-w-0 pr-1">
                <p className="text-sm font-700 text-gray-900">Adicione à tela inicial</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                  Acesse o PandaPro com um toque, como um app nativo — sem precisar abrir o navegador.
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="w-7 h-7 flex items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 cursor-pointer transition-colors shrink-0"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleDismiss}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-2xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleAdd}
                disabled={installState === 'installing'}
                className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-2xl text-sm cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {installState === 'installing' ? (
                  <><i className="ri-loader-4-line animate-spin text-sm" />Instalando...</>
                ) : (
                  <><i className={isIOSDevice ? 'ri-share-forward-line' : 'ri-add-circle-line'} />Adicionar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iOS / manual instructions modal */}
      <IOSInstructionsModal open={showIOSModal} onClose={closeIOSModal} />
    </>
  );
};

// ─── Exported component (always mounted, internally decides what to show) ─────
export const InstallPWABanner = () => {
  return <InstallBannerContent />;
};

// ─── Reusable iOS modal for Settings page ─────────────────────────────────────
export { IOSInstructionsModal };
